import { useCallback, useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box } from '@mui/material';

interface SyncedChartProps {
  options: Highcharts.Options;
  chartIndex: number;
  onSyncCrosshair: (sourceIndex: number, e: MouseEvent) => void;
  onHideCrosshairs: () => void;
  onSyncZoom: (sourceIndex: number, min: number | undefined, max: number | undefined) => void;
  registerChart: (index: number) => (ref: { chart: Highcharts.Chart } | null) => void;
}

export const SyncedChart = ({
  options,
  chartIndex,
  onSyncCrosshair,
  onHideCrosshairs,
  onSyncZoom,
  registerChart,
}: SyncedChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const register = registerChart(chartIndex);

  const handleCallback = useCallback(
    (chart: Highcharts.Chart) => {
      register({ chart });
    },
    [register],
  );

  // Wire up mouse events for crosshair sync
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      onSyncCrosshair(chartIndex, e);
    };
    const handleMouseLeave = () => {
      onHideCrosshairs();
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [chartIndex, onSyncCrosshair, onHideCrosshairs]);

  // Merge afterSetExtremes — the shared syncingRef in useSyncedCharts prevents loops
  const mergedOptions: Highcharts.Options = {
    ...options,
    xAxis: {
      ...(options.xAxis as Highcharts.XAxisOptions),
      events: {
        afterSetExtremes(this: Highcharts.Axis, e: Highcharts.AxisSetExtremesEventObject) {
          onSyncZoom(chartIndex, e.min, e.max);
        },
      },
    },
  };

  return (
    <Box ref={containerRef}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={mergedOptions}
        callback={handleCallback}
      />
    </Box>
  );
};
